/**
 * One palette and one type scale for every 3PL screen.
 *
 * WHY THIS EXISTS
 * ---------------
 * Ten of the eleven views in this folder opened with their own near-identical
 * `const colors = { ... }`, and they had drifted: amber was #ca8a04 in
 * StaffList and #f59e0b in four others, red was #dc2626 almost everywhere and
 * #ef4444 in PartnerDetails, purple was #7c3aed in two files and #8b5cf6 in a
 * third. Fonts were worse than drifted, they were unmanaged: CreatePartner was
 * the only file naming a stack, so it rendered in a different typeface from
 * every screen beside it, and two others dropped to bare generic `monospace`
 * for id columns.
 *
 * Import from here instead of declaring a palette. A colour that is not in
 * this file does not belong on a 3PL screen.
 */

/** Brand and state colours. */
export const colors = {
  // Brand
  green: "#006202",
  greenSoft: "#E8F3E9",
  blue: "#023485",
  blueSoft: "#E8EEF9",

  // State. One meaning each, used nowhere else:
  // amber warns, red refuses, teal informs, green confirms.
  amber: "#B45309",
  amberSoft: "#FEF3C7",
  red: "#B91C1C",
  redSoft: "#FEE2E2",
  teal: "#0F766E",
  tealSoft: "#CCFBF1",

  // Ink and ground
  ink: "#111827",
  body: "#374151",
  muted: "#6B7280",
  faint: "#9CA3AF",
  line: "#E5E7EB",
  lineSoft: "#F3F4F6",
  surface: "#FFFFFF",
  ground: "#F9FAFB",
  white: "#FFFFFF",
};

/**
 * The type stack. Named once so no screen has to guess, and so the id columns
 * that want a monospace face get the SAME monospace face as each other.
 */
export const font = {
  sans: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  mono: "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, Consolas, monospace",
};

/** Type scale. Sizes are the ones already in use, deduplicated. */
export const text = {
  // Section eyebrow: uppercase, tracked, quiet.
  section: {
    fontFamily: font.mono,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: colors.faint,
    margin: "0 0 20px",
  },
  label: {
    fontFamily: font.sans,
    display: "block",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: colors.muted,
    marginBottom: 5,
  },
  title: { fontFamily: font.sans, fontSize: 20, fontWeight: 700, color: colors.ink, margin: 0 },
  subtitle: { fontFamily: font.sans, fontSize: 13, color: colors.muted, margin: "4px 0 0" },
  body: { fontFamily: font.sans, fontSize: 14, color: colors.body },
  help: { fontFamily: font.sans, fontSize: 11, color: colors.faint, margin: "4px 0 0" },
  // Anything the reader compares down a column: ids, money, counts.
  numeric: { fontFamily: font.mono, fontSize: 13, color: colors.ink, fontVariantNumeric: "tabular-nums" },
};

/** Surfaces. */
export const card = {
  background: colors.surface,
  borderRadius: 14,
  border: `1px solid ${colors.line}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  marginBottom: 16,
};
export const cardBody = { padding: "24px 28px" };

export const grid = (cols, gap = 20) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
  gap: `0 ${gap}px`,
});

/** Inputs, matching the shape CreatePartner established. */
export const inputBase = {
  fontFamily: font.sans,
  width: "100%",
  padding: "9px 12px",
  fontSize: 14,
  border: `1px solid ${colors.line}`,
  borderRadius: 8,
  outline: "none",
  background: "#FAFAFA",
  color: colors.ink,
  transition: "border-color 0.15s, background 0.15s",
  boxSizing: "border-box",
};

const onFocus = (e) => {
  e.target.style.borderColor = colors.green;
  e.target.style.background = colors.surface;
};
const onBlur = (e) => {
  e.target.style.borderColor = colors.line;
  e.target.style.background = "#FAFAFA";
};

/** Spread onto an <input>/<select> to get the standard field. */
export const inp = (extra = {}) => ({ style: { ...inputBase, ...extra }, onFocus, onBlur });

/** Buttons. `kind` picks intent, never decoration. */
export const button = (kind = "primary", extra = {}) => {
  const base = {
    fontFamily: font.sans,
    padding: "9px 20px",
    borderRadius: 9,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid transparent",
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    lineHeight: 1.2,
  };
  const kinds = {
    primary: { background: colors.green, color: colors.white },
    secondary: { background: colors.ground, color: colors.body, borderColor: colors.line },
    danger: { background: colors.red, color: colors.white },
    quiet: { background: "transparent", color: colors.muted, borderColor: colors.line },
  };
  return { ...base, ...(kinds[kind] || kinds.primary), ...extra };
};

/**
 * A status pill. `tone` maps to the state colours above, so the same condition
 * reads the same on every screen.
 */
export const pill = (tone = "muted") => {
  const tones = {
    good: { background: colors.greenSoft, color: colors.green },
    warn: { background: colors.amberSoft, color: colors.amber },
    bad: { background: colors.redSoft, color: colors.red },
    info: { background: colors.tealSoft, color: colors.teal },
    muted: { background: colors.lineSoft, color: colors.muted },
  };
  return {
    fontFamily: font.sans,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    ...(tones[tone] || tones.muted),
  };
};

/** UGX, formatted the one way. */
export const ugx = (n) =>
  `UGX ${Number(n || 0).toLocaleString("en-UG", { maximumFractionDigits: 0 })}`;

export default { colors, font, text, card, cardBody, grid, inp, inputBase, button, pill, ugx };
